import {
  EN_442_RATING_BASIS,
  EN_1264_UNDERFLOOR_LIMITS,
  MIS_3005_D_SIZING,
  RADIATOR_EXPONENT_DEFAULTS,
  RULES_BUNDLE_VERSION,
  type RadiatorKind,
  type UnderfloorZone,
} from "@sts/rules-data";
import { ComplianceError, ENGINE_VERSION, type RoomHeatLoss } from "./heat-loss.js";
import type { CalcStamp, CalcWarning, Provenanced } from "./types.js";

/**
 * Emitter sizing and the reuse-versus-replace decision.
 *
 * This is the calculation that decides the price of a heat pump job and whether a
 * low flow temperature is achievable at all. A radiator quoted at 2000 W is quoted
 * at BS EN 442's ΔT50 — 70°C mean water into a 20°C room. Drop the mean water to
 * 47°C in a 22°C bathroom and the same radiator delivers 812 W. Nothing about the
 * radiator changed; only the driving temperature difference did.
 *
 * The correction is a power law, output = rated × (ΔT_actual / ΔT_rated)^n, because
 * a radiator's output is part radiant and part convective and the convective part
 * is itself driven by buoyancy that grows with ΔT. Treating it as linear (n = 1)
 * overstates a retained radiator at 45°C flow by roughly a quarter, which is the
 * difference between "keep your radiators" and "replace nine of them".
 *
 * Underfloor heating is deliberately NOT put through this power law — see
 * `underfloorOutputCeiling` for why, and what is done instead.
 */

/**
 * Above this uplift a "same type, longer" swap usually stops being practical: a
 * radiator twice the length rarely fits the wall it is on. This threshold changes
 * the WORDING of the advice only — it never enters a number in the result. It is a
 * rule of thumb from practice rather than a value from any standard, which is why
 * it lives here and not in rules-data.
 */
const SAME_TYPE_UPLIFT_PRACTICAL_LIMIT = 2;

function round(n: number, dp = 6): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

// ---------------------------------------------------------------------------
// Correcting a rated output to actual conditions
// ---------------------------------------------------------------------------

export interface CorrectEmitterOutputInput {
  /** Manufacturer's quoted output at `ratedDeltaTK`, W. */
  readonly ratedOutputW: number;
  readonly flowTempC: number;
  readonly returnTempC: number;
  /**
   * The room's internal design temperature, °C — NOT a fixed 20°C.
   *
   * This is the term people forget. A bathroom held at 22°C has 4 K less driving
   * temperature difference than a bedroom at 18°C fed by the same pipework, so the
   * same radiator delivers materially less there. Bathrooms are consequently the
   * rooms that most often set the whole system's flow temperature.
   */
  readonly roomDesignTempC: number;
  /** The characteristic exponent n for this emitter. */
  readonly exponent: number;
  /** Defaults to the BS EN 442 ΔT50 basis. Older BS 3528 datasheets use 60. */
  readonly ratedDeltaTK?: number;
}

export interface CorrectedEmitterOutput {
  readonly meanWaterTempC: number;
  /** Mean water temperature minus room design temperature, K. */
  readonly deltaTActualK: number;
  readonly ratedDeltaTK: number;
  readonly exponent: number;
  /**
   * (ΔT_actual / ΔT_rated)^n. Deliberately NOT rounded, unlike the temperatures and
   * watts either side of it: replacement sizing DIVIDES by this factor, so rounding
   * it for presentation would put the rounding error back into a calculated result.
   * Display sites round it themselves at the point of display.
   */
  readonly correctionFactor: number;
  readonly outputW: number;
  /** The arithmetic, line by line, so a person can repeat it on a calculator. */
  readonly working: readonly string[];
}

/**
 * output = rated × (ΔT_actual / ΔT_rated)^n
 *
 * with ΔT_actual = (flow + return)/2 − room, the ARITHMETIC mean water temperature
 * that BS EN 442 and UK domestic practice use.
 *
 * THE SOURCE, and how far it is checked. BS EN 442-2 rates a radiator at standard
 * conditions of 75 °C flow / 65 °C return / 20 °C air — a 50 K excess of mean water
 * over air, hence "ΔT50" — and gives the characteristic equation Φ = Km·ΔT^n, whose
 * Km and n come from that product's own laboratory test. Dividing the equation by
 * itself at the rated conditions cancels Km and leaves the ratio form above, which
 * is why no product constant is needed here beyond the rating and n.
 *
 * The standard itself is paywalled and has NOT been read: the statement above is
 * taken from secondary sources, chiefly the REHVA Journal's open-access "Can we
 * still trust in EN 442?" Parts 1-2, which also measured n = 1.26-1.35 across type 22
 * radiators — the band the provisional default n = 1.3 in `RADIATOR_EXPONENT_DEFAULTS`
 * sits in the middle of, and which Purmo's own published 1.3358 for a K2 600 falls
 * inside. That is why both rulesets are `provenance: "provisional"` and why a
 * compliance-grade run refuses to use the defaults. Full working, the sources, and
 * the arithmetic-versus-logarithmic excess-temperature question (worth 0.6% at a
 * 5 K system ΔT, 25% at 20 K) are in
 * docs/research/flow-temperature-emitter-rerating-2026-09-14.md.
 */
export function correctEmitterOutput(
  input: CorrectEmitterOutputInput,
): CorrectedEmitterOutput {
  const ratedDeltaTK = input.ratedDeltaTK ?? EN_442_RATING_BASIS.data.ratedDeltaTK;
  const meanWaterTempC = (input.flowTempC + input.returnTempC) / 2;
  const deltaTActualK = meanWaterTempC - input.roomDesignTempC;

  const working: string[] = [
    `Mean water temperature = (${input.flowTempC} + ${input.returnTempC}) / 2 = ${round(meanWaterTempC, 3)} °C`,
    `ΔT to room = ${round(meanWaterTempC, 3)} − ${input.roomDesignTempC} = ${round(deltaTActualK, 3)} K` +
      ` (room design temperature ${input.roomDesignTempC} °C)`,
  ];

  // A power law with a fractional exponent is undefined for a negative base, and
  // physically there is nothing to correct: water no warmer than the room heats
  // nothing. Report zero rather than NaN so the shortfall reads as the full load.
  if (deltaTActualK <= 0) {
    working.push(
      "Mean water temperature is not above the room design temperature, so this emitter delivers no useful heat.",
    );
    return {
      meanWaterTempC: round(meanWaterTempC),
      deltaTActualK: round(deltaTActualK),
      ratedDeltaTK,
      exponent: input.exponent,
      correctionFactor: 0,
      outputW: 0,
      working,
    };
  }

  const ratio = deltaTActualK / ratedDeltaTK;
  const correctionFactor = ratio ** input.exponent;
  const outputW = input.ratedOutputW * correctionFactor;

  working.push(
    `Correction factor = (${round(deltaTActualK, 3)} / ${ratedDeltaTK}) ^ ${input.exponent} = ${round(correctionFactor, 4)}`,
    `Output = ${input.ratedOutputW} W × ${round(correctionFactor, 4)} = ${round(outputW, 1)} W`,
  );

  return {
    meanWaterTempC: round(meanWaterTempC),
    deltaTActualK: round(deltaTActualK),
    ratedDeltaTK,
    exponent: input.exponent,
    correctionFactor,
    outputW: round(outputW),
    working,
  };
}

export interface FlowTempForOutputInput {
  /** The output the emitter has to produce, W — normally the room's heat loss. */
  readonly requiredOutputW: number;
  readonly ratedOutputW: number;
  readonly roomDesignTempC: number;
  readonly exponent: number;
  readonly ratedDeltaTK?: number;
  /** Design flow-minus-return across the system, K. Heat pumps commonly use 5. */
  readonly systemDeltaTK: number;
}

export interface FlowTempForOutput {
  readonly requiredDeltaTActualK: number;
  readonly requiredMeanWaterTempC: number;
  readonly requiredFlowTempC: number;
  readonly requiredReturnTempC: number;
  readonly working: readonly string[];
}

/**
 * The inverse of `correctEmitterOutput`: the lowest flow temperature at which this
 * emitter covers the required output.
 *
 *   ΔT_required = ΔT_rated × (required / rated)^(1/n)
 *   mean water  = room + ΔT_required
 *   flow        = mean + systemΔT / 2
 *
 * This is what makes the "worst performing room" answerable: run it for every room
 * and take the maximum.
 */
export function flowTempForRequiredOutput(
  input: FlowTempForOutputInput,
): FlowTempForOutput {
  if (!(input.ratedOutputW > 0)) {
    throw new RangeError(
      "A rated output greater than zero is needed to work back to a flow temperature.",
    );
  }
  if (!(input.systemDeltaTK > 0)) {
    throw new RangeError(
      "The system flow-minus-return difference must be greater than zero.",
    );
  }

  const ratedDeltaTK = input.ratedDeltaTK ?? EN_442_RATING_BASIS.data.ratedDeltaTK;
  const outputRatio = Math.max(input.requiredOutputW, 0) / input.ratedOutputW;
  const requiredDeltaTActualK = ratedDeltaTK * outputRatio ** (1 / input.exponent);
  const requiredMeanWaterTempC = input.roomDesignTempC + requiredDeltaTActualK;
  const requiredFlowTempC = requiredMeanWaterTempC + input.systemDeltaTK / 2;
  const requiredReturnTempC = requiredMeanWaterTempC - input.systemDeltaTK / 2;

  return {
    requiredDeltaTActualK: round(requiredDeltaTActualK),
    requiredMeanWaterTempC: round(requiredMeanWaterTempC),
    requiredFlowTempC: round(requiredFlowTempC),
    requiredReturnTempC: round(requiredReturnTempC),
    working: [
      `Output ratio = ${round(input.requiredOutputW, 1)} W / ${input.ratedOutputW} W = ${round(outputRatio, 4)}`,
      `Required ΔT = ${ratedDeltaTK} × ${round(outputRatio, 4)} ^ (1 / ${input.exponent}) = ${round(requiredDeltaTActualK, 2)} K`,
      `Required mean water temperature = ${input.roomDesignTempC} + ${round(requiredDeltaTActualK, 2)} = ${round(requiredMeanWaterTempC, 2)} °C`,
      `Required flow temperature = ${round(requiredMeanWaterTempC, 2)} + ${input.systemDeltaTK} / 2 = ${round(requiredFlowTempC, 2)} °C` +
        ` (return ${round(requiredReturnTempC, 2)} °C)`,
    ],
  };
}

// ---------------------------------------------------------------------------
// Underfloor heating
// ---------------------------------------------------------------------------

export interface UnderfloorCeilingInput {
  readonly heatedFloorAreaM2: number;
  readonly roomDesignTempC: number;
  readonly zone?: UnderfloorZone;
}

export interface UnderfloorCeiling {
  readonly zone: UnderfloorZone;
  readonly maxSurfaceTempC: number;
  readonly surfaceToAirDeltaTK: number;
  readonly maxSpecificOutputWPerM2: number;
  readonly maxOutputW: number;
  readonly working: readonly string[];
}

/**
 * The most a heated floor can ever deliver into this room, W.
 *
 * Underfloor heating is NOT modelled with the BS EN 442 exponent, and the reason is
 * not fussiness. A radiator's output is limited by the water temperature, so raising
 * the flow raises the output. A floor's output is limited by its SURFACE temperature,
 * which BS EN 1264-3 caps at 29°C in occupied areas for comfort and floor coverings.
 * Past that cap, raising the flow temperature buys nothing at all. A power law in
 * water temperature has no such ceiling and would happily promise 250 W/m² from a
 * floor that can physically deliver 100.
 *
 * So the honest, useful thing this engine can compute from survey data alone is the
 * ceiling: BS EN 1264-2's characteristic curve q = 8.92 × (θ_floor − θ_room)^1.1
 * evaluated at the limiting surface temperature. That answers "can this floor ever
 * meet this room's load?" without needing pipe spacing, screed conductivity or floor
 * covering resistance.
 *
 * What it deliberately does NOT do is tell you the flow temperature the floor needs.
 * Getting from mean water temperature to floor surface temperature requires the
 * BS EN 1264 construction parameters — pipe pitch and diameter, screed cover and
 * conductivity, and above all the floor covering's thermal resistance, which alone
 * can move the answer by 10 K. None of that comes out of a LiDAR survey. Rather than
 * guess it, `assessEmitters` requires the underfloor designer's own figures and
 * refuses to state a flow temperature without them.
 */
export function underfloorOutputCeiling(
  input: UnderfloorCeilingInput,
): UnderfloorCeiling {
  const limits = EN_1264_UNDERFLOOR_LIMITS.data;
  const zone = input.zone ?? "occupied";
  const maxSurfaceTempC = limits.maxSurfaceTempC[zone];
  const surfaceToAirDeltaTK = maxSurfaceTempC - input.roomDesignTempC;

  if (surfaceToAirDeltaTK <= 0) {
    return {
      zone,
      maxSurfaceTempC,
      surfaceToAirDeltaTK: round(surfaceToAirDeltaTK),
      maxSpecificOutputWPerM2: 0,
      maxOutputW: 0,
      working: [
        `BS EN 1264-3 limits the floor surface to ${maxSurfaceTempC} °C in a "${zone}" area, which is` +
          ` not above this room's ${input.roomDesignTempC} °C design temperature. A heated floor cannot` +
          ` heat this room at all.`,
      ],
    };
  }

  const maxSpecificOutputWPerM2 =
    limits.characteristicCoefficient *
    surfaceToAirDeltaTK ** limits.characteristicExponent;
  const maxOutputW = maxSpecificOutputWPerM2 * input.heatedFloorAreaM2;

  return {
    zone,
    maxSurfaceTempC,
    surfaceToAirDeltaTK: round(surfaceToAirDeltaTK),
    maxSpecificOutputWPerM2: round(maxSpecificOutputWPerM2),
    maxOutputW: round(maxOutputW),
    working: [
      `BS EN 1264-3 surface temperature limit for a "${zone}" area = ${maxSurfaceTempC} °C`,
      `Surface-to-air ΔT = ${maxSurfaceTempC} − ${input.roomDesignTempC} = ${round(surfaceToAirDeltaTK, 2)} K`,
      `BS EN 1264-2 characteristic curve: q = ${limits.characteristicCoefficient} × ${round(surfaceToAirDeltaTK, 2)}` +
        ` ^ ${limits.characteristicExponent} = ${round(maxSpecificOutputWPerM2, 1)} W/m²`,
      `Ceiling output = ${round(maxSpecificOutputWPerM2, 1)} W/m² × ${input.heatedFloorAreaM2} m² = ${round(maxOutputW, 1)} W`,
      "This is a ceiling set by floor surface temperature, not by water temperature — raising the flow temperature above the point that reaches it achieves nothing.",
    ],
  };
}

// ---------------------------------------------------------------------------
// Per-room assessment
// ---------------------------------------------------------------------------

export interface RadiatorEmitter {
  readonly kind: "radiator";
  readonly id: string;
  readonly roomId: string;
  /** What a designer would recognise on a schedule, e.g. "Type 22 600 × 1200". */
  readonly description: string;
  readonly radiatorKind?: RadiatorKind;
  /**
   * Output at `ratedDeltaTK`, W, wrapped in its provenance. The whole reuse
   * decision rests on this number, so where it came from is part of the result:
   * "manufacturer" means a datasheet, anything else means somebody's estimate.
   */
  readonly ratedOutputW: Provenanced<number>;
  /** From the datasheet. Omit to fall back to the (provisional) ΔT50 default. */
  readonly ratedDeltaTK?: number;
  /** From the datasheet. Omit to fall back to the (provisional) kind default. */
  readonly exponent?: number;
  readonly heightMm?: number;
  /** Needed to suggest a longer replacement of the same type. */
  readonly lengthMm?: number;
}

export interface UnderfloorEmitter {
  readonly kind: "underfloor";
  readonly id: string;
  readonly roomId: string;
  readonly description: string;
  readonly heatedFloorAreaM2: number;
  /** Defaults to "occupied", the most restrictive BS EN 1264-3 limit. */
  readonly zone?: UnderfloorZone;
  /**
   * The underfloor designer's own output figure at their own stated flow and
   * return. Without it this engine will not state a flow temperature for the room
   * — see `underfloorOutputCeiling` for why it cannot be derived from survey data.
   */
  readonly manufacturerDesign?: {
    readonly flowTempC: number;
    readonly returnTempC: number;
    readonly outputW: number;
    /** Where the figure came from, e.g. "Nu-Heat design ref 41822 rev B". */
    readonly source: string;
  };
}

export type ExistingEmitter = RadiatorEmitter | UnderfloorEmitter;

/**
 * "retain" and "replace" map onto the design pack's emitter actions directly;
 * "install-new" is the pack's "new". "undetermined" cannot be written into a design
 * pack at all — it means this engine declined to guess and a person must decide.
 */
export type EmitterVerdict = "retain" | "replace" | "install-new" | "undetermined";

export interface ReplacementSuggestion {
  /** What the replacement must be rated at, at `ratedDeltaTK`, W. */
  readonly requiredRatedOutputW: number;
  readonly ratedDeltaTK: number;
  /** Required rated output divided by the existing one. */
  readonly upliftFactor: number;
  /**
   * Minimum length of a same-height, same-type radiator, mm. Assumes output is
   * proportional to length at constant height and type — the standard planning
   * approximation, to be confirmed against the chosen product's datasheet.
   */
  readonly suggestedMinimumLengthMm?: number;
  /**
   * The other lever: keep this radiator and run the system hotter. Reported so the
   * designer can price the trade against the efficiency it costs.
   */
  readonly alternativeFlowTempC?: number;
  readonly note: string;
}

export interface RoomEmitterAssessment {
  readonly roomId: string;
  readonly roomName: string;
  readonly designTempC: number;
  readonly heatLossW: number;
  readonly emitterId?: string;
  readonly emitterDescription?: string;
  readonly modelledAs: "radiator" | "underfloor" | "none";
  /** Output at the proposed flow temperature, W. Undefined when undetermined. */
  readonly outputAtProposedFlowW?: number;
  /** Output as a percentage of the room's heat loss. 100 = exactly enough. */
  readonly outputPercentOfLoad?: number;
  /** Watts short of the room's heat loss. Zero when the emitter is adequate. */
  readonly shortfallW: number;
  /** Shortfall as a percentage of the room's heat loss. */
  readonly shortfallPercent: number;
  /** Watts in hand when the emitter is more than adequate. */
  readonly surplusW: number;
  readonly meetsLoad: boolean;
  /** Lowest flow temperature at which THIS emitter covers the room, °C. */
  readonly requiredFlowTempC?: number;
  readonly verdict: EmitterVerdict;
  /** Plain prose a designer can read out to a customer. */
  readonly reasons: readonly string[];
  /** The arithmetic, so the number can be checked by hand. */
  readonly working: readonly string[];
  readonly replacement?: ReplacementSuggestion;
  /** Underfloor only: the BS EN 1264-3 surface-temperature-limited ceiling. */
  readonly underfloorCeiling?: UnderfloorCeiling;
}

export interface EmitterAssessmentInput {
  /** Room-by-room heat losses, straight from `calculateDwellingHeatLoss`. */
  readonly rooms: readonly RoomHeatLoss[];
  readonly emitters: readonly ExistingEmitter[];
  /** The flow temperature the design proposes, °C. */
  readonly proposedFlowTempC: number;
  /** Design flow-minus-return across the system, K. Heat pumps commonly use 5. */
  readonly systemDeltaTK: number;
  /**
   * How far short an emitter may fall and still be kept, as a percentage of the
   * room's load. Defaults to zero: a tolerance is a design decision the designer
   * owns and signs for, not something this engine should apply quietly.
   */
  readonly shortfallTolerancePercent?: number;
  readonly complianceMode: boolean;
  readonly calculationDate: string;
}

export interface EmitterAssessment {
  readonly proposedFlowTempC: number;
  readonly proposedReturnTempC: number;
  readonly systemDeltaTK: number;
  readonly shortfallTolerancePercent: number;
  readonly rooms: readonly RoomEmitterAssessment[];
  /**
   * The room that sets the design emitter temperature — the one needing the
   * highest flow temperature if its emitter is kept. MIS 3005-D §3.8.1(d) requires
   * the design pack to state the design emitter temperature on this basis.
   */
  readonly worstPerformingRoom?: {
    readonly roomId: string;
    readonly roomName: string;
    readonly designTempC: number;
    readonly requiredFlowTempC: number;
  };
  /**
   * Flow temperature the whole system needs if every existing emitter is retained,
   * °C. Undefined when any room's requirement could not be determined — in which
   * case `roomsWithUndeterminedRequirement` names them.
   */
  readonly systemFlowTempIfAllRetainedC?: number;
  readonly roomsWithUndeterminedRequirement: readonly string[];
  /** The commercial answer: which rooms need a new emitter at the proposed flow. */
  readonly roomsToReplace: readonly string[];
  readonly roomsWithoutEmitters: readonly string[];
  /** The room furthest short at the proposed flow temperature, by percentage. */
  readonly largestShortfallRoom?: {
    readonly roomId: string;
    readonly roomName: string;
    readonly shortfallW: number;
    readonly shortfallPercent: number;
  };
  readonly roomsMeetingLoad: number;
  readonly roomsShort: number;
  readonly warnings: readonly CalcWarning[];
  readonly stamp: CalcStamp;
}

/**
 * Assesses every room's existing emitter against its heat loss at a proposed flow
 * temperature, and answers the two questions a heat pump design turns on: which
 * emitters have to change, and what flow temperature the system needs if none do.
 *
 * Compliance mode behaves as `checkSizing` does — it throws `ComplianceError`
 * rather than return a result built on a compliance failure. The failures specific
 * to this calculation are: a room with no emitter at all; an underfloor room whose
 * flow temperature this engine will not guess; a room whose load exceeds what a
 * heated floor can physically deliver; and — the house rule about provisional data
 * — any radiator whose exponent or rating basis fell back to the built-in defaults
 * rather than coming from its datasheet.
 */
export function assessEmitters(input: EmitterAssessmentInput): EmitterAssessment {
  const warnings: CalcWarning[] = [];
  const tolerancePercent = input.shortfallTolerancePercent ?? 0;
  const proposedReturnTempC = input.proposedFlowTempC - input.systemDeltaTK;

  if (!(input.systemDeltaTK > 0)) {
    warnings.push({
      code: "INVALID_SYSTEM_DELTA_T",
      severity: "compliance-failure",
      message:
        `The system flow-minus-return difference is ${input.systemDeltaTK} K. It must be greater ` +
        "than zero — the mean water temperature and every emitter output depend on it.",
    });
  }

  const byRoom = new Map<string, ExistingEmitter>();
  for (const e of input.emitters) {
    if (byRoom.has(e.roomId)) {
      warnings.push({
        code: "MULTIPLE_EMITTERS_IN_ROOM",
        severity: "warning",
        subjectId: e.roomId,
        message:
          `More than one emitter was supplied for room "${e.roomId}". Only the first is assessed. ` +
          "Combine multiple emitters in a room into a single entry with their summed rated output.",
      });
      continue;
    }
    byRoom.set(e.roomId, e);
  }

  const knownRoomIds = new Set(input.rooms.map((r) => r.roomId));
  for (const e of input.emitters) {
    if (!knownRoomIds.has(e.roomId)) {
      warnings.push({
        code: "EMITTER_FOR_UNKNOWN_ROOM",
        severity: "warning",
        subjectId: e.id,
        message:
          `Emitter "${e.description}" is assigned to room "${e.roomId}", which is not in the heat ` +
          "loss calculation. It has been ignored.",
      });
    }
  }

  const rooms = input.rooms.map((room) =>
    assessRoom(room, byRoom.get(room.roomId), input, tolerancePercent, warnings),
  );

  const roomsWithoutEmitters = rooms
    .filter((r) => r.modelledAs === "none")
    .map((r) => r.roomId);
  const roomsToReplace = rooms
    .filter((r) => r.verdict === "replace")
    .map((r) => r.roomId);

  // §3.8.1(d): the design emitter temperature is set by the worst performing room,
  // so the system flow temperature for a full-retention design is the maximum of
  // the per-room requirements. A room with no emitter has nothing to retain and so
  // does not constrain it; a room whose requirement is undetermined does, because
  // it could turn out to be the worst one.
  const constrainingRooms = rooms.filter(
    (r) => r.modelledAs !== "none" && r.requiredFlowTempC !== undefined,
  );
  const roomsWithUndeterminedRequirement = rooms
    .filter((r) => r.modelledAs !== "none" && r.requiredFlowTempC === undefined)
    .map((r) => r.roomId);

  let worstPerformingRoom: EmitterAssessment["worstPerformingRoom"];
  for (const r of constrainingRooms) {
    const required = r.requiredFlowTempC as number;
    if (
      worstPerformingRoom === undefined ||
      required > worstPerformingRoom.requiredFlowTempC
    ) {
      worstPerformingRoom = {
        roomId: r.roomId,
        roomName: r.roomName,
        designTempC: r.designTempC,
        requiredFlowTempC: required,
      };
    }
  }

  const systemFlowTempIfAllRetainedC =
    roomsWithUndeterminedRequirement.length === 0 && worstPerformingRoom !== undefined
      ? worstPerformingRoom.requiredFlowTempC
      : undefined;

  if (roomsWithUndeterminedRequirement.length > 0) {
    warnings.push({
      code: "SYSTEM_FLOW_TEMPERATURE_UNDETERMINED",
      severity: "warning",
      message:
        "The flow temperature for a fully retained system could not be determined because " +
        `${roomsWithUndeterminedRequirement.join(", ")} has an emitter whose requirement is unknown. ` +
        "The worst performing room reported is the worst of those that could be assessed, and may " +
        "not be the worst overall.",
    });
  }

  let largestShortfallRoom: EmitterAssessment["largestShortfallRoom"];
  for (const r of rooms) {
    if (r.shortfallW <= 0) continue;
    if (
      largestShortfallRoom === undefined ||
      r.shortfallPercent > largestShortfallRoom.shortfallPercent
    ) {
      largestShortfallRoom = {
        roomId: r.roomId,
        roomName: r.roomName,
        shortfallW: r.shortfallW,
        shortfallPercent: r.shortfallPercent,
      };
    }
  }

  // Cross-reference to the sizing rules: a retained-emitter design that needs more
  // than 55°C is not merely inefficient, it triggers MIS 3005-D §3.4.5's obligation
  // to offer an alternative design at or below 55°C.
  const thresholdC = MIS_3005_D_SIZING.data.alternativeDesignFlowTempThresholdC;
  if (
    systemFlowTempIfAllRetainedC !== undefined &&
    systemFlowTempIfAllRetainedC > thresholdC
  ) {
    warnings.push({
      code: "RETAINED_EMITTERS_EXCEED_55C",
      severity: "warning",
      message:
        `Retaining every existing emitter would need ${round(systemFlowTempIfAllRetainedC, 1)}°C flow, ` +
        `set by "${worstPerformingRoom?.roomName}". Above ${thresholdC}°C, MIS 3005-D §3.4.5 requires an ` +
        `alternative design at or below ${thresholdC}°C to be offered, so emitter upgrades in the worst ` +
        "rooms have to be priced either way.",
    });
  }

  const roomsShort = rooms.filter((r) => !r.meetsLoad).length;

  if (input.complianceMode) {
    const failures = warnings.filter((w) => w.severity === "compliance-failure");
    if (failures.length > 0) {
      throw new ComplianceError(
        "Emitter assessment cannot be issued in compliance mode.",
        failures,
      );
    }
  }

  return {
    proposedFlowTempC: input.proposedFlowTempC,
    proposedReturnTempC: round(proposedReturnTempC),
    systemDeltaTK: input.systemDeltaTK,
    shortfallTolerancePercent: tolerancePercent,
    rooms,
    worstPerformingRoom,
    systemFlowTempIfAllRetainedC,
    roomsWithUndeterminedRequirement,
    roomsToReplace,
    roomsWithoutEmitters,
    largestShortfallRoom,
    roomsMeetingLoad: rooms.length - roomsShort,
    roomsShort,
    warnings,
    stamp: {
      engineVersion: ENGINE_VERSION,
      rulesBundleVersion: RULES_BUNDLE_VERSION,
      calculatedAt: input.calculationDate,
      complianceMode: input.complianceMode,
    },
  };
}

function assessRoom(
  room: RoomHeatLoss,
  emitter: ExistingEmitter | undefined,
  input: EmitterAssessmentInput,
  tolerancePercent: number,
  warnings: CalcWarning[],
): RoomEmitterAssessment {
  const heatLossW = room.totalW;

  if (emitter === undefined) {
    warnings.push({
      code: "MIS3005D_ROOM_WITHOUT_EMITTER",
      severity: "compliance-failure",
      subjectId: room.roomId,
      message:
        `Room "${room.name}" has no emitter. MIS 3005-D §3.8.1(c) requires emitter types and ` +
        "dimensions for the design, so every room needs either an existing emitter to assess or a " +
        "new one specified.",
    });
    return {
      roomId: room.roomId,
      roomName: room.name,
      designTempC: room.designTempC,
      heatLossW,
      modelledAs: "none",
      shortfallW: round(Math.max(heatLossW, 0)),
      shortfallPercent: heatLossW > 0 ? 100 : 0,
      surplusW: 0,
      meetsLoad: false,
      verdict: "install-new",
      reasons: [
        `No emitter was recorded in ${room.name}, so its ${round(heatLossW, 0)} W heat loss is currently unmet.`,
        "A new emitter has to be specified; size it for the design flow temperature rather than working back from an existing one.",
      ],
      working: [
        `Room heat loss = ${round(heatLossW, 1)} W`,
        "No emitter recorded, so there is nothing to correct.",
      ],
    };
  }

  return emitter.kind === "radiator"
    ? assessRadiatorRoom(room, emitter, input, tolerancePercent, warnings)
    : assessUnderfloorRoom(room, emitter, input, warnings);
}

function assessRadiatorRoom(
  room: RoomHeatLoss,
  emitter: RadiatorEmitter,
  input: EmitterAssessmentInput,
  tolerancePercent: number,
  warnings: CalcWarning[],
): RoomEmitterAssessment {
  const heatLossW = room.totalW;
  const radiatorKind =
    emitter.radiatorKind ?? RADIATOR_EXPONENT_DEFAULTS.data.defaultKind;

  const exponentFromDatasheet = emitter.exponent !== undefined;
  const exponent =
    emitter.exponent ?? RADIATOR_EXPONENT_DEFAULTS.data.byKind[radiatorKind];

  const ratedDeltaTFromDatasheet = emitter.ratedDeltaTK !== undefined;
  const ratedDeltaTK = emitter.ratedDeltaTK ?? EN_442_RATING_BASIS.data.ratedDeltaTK;

  // The house rule: compliance-grade results never come from provisional data. The
  // exponent and the rating basis in rules-data are provisional, so a compliance run
  // has to take them from the product's datasheet. Development and on-site what-if
  // runs use the defaults freely — that is what they are for.
  if (!exponentFromDatasheet) {
    warnings.push({
      code: "EMITTER_EXPONENT_ASSUMED",
      severity: "compliance-failure",
      subjectId: emitter.id,
      message:
        `"${emitter.description}" in ${room.name} has no exponent from its datasheet, so the ` +
        `provisional default n = ${exponent} for a ${radiatorKind} was used ` +
        `(${RADIATOR_EXPONENT_DEFAULTS.source.standard}, ruleset ${RADIATOR_EXPONENT_DEFAULTS.version}). ` +
        "Real exponents run from about 1.2 to 1.35 for panel radiators, which moves the output at " +
        "45°C flow by roughly 7% either way.",
    });
  }
  if (!ratedDeltaTFromDatasheet) {
    warnings.push({
      code: "EMITTER_RATING_BASIS_ASSUMED",
      severity: "compliance-failure",
      subjectId: emitter.id,
      message:
        `"${emitter.description}" in ${room.name} does not state the ΔT its rated output applies at, ` +
        `so the provisional BS EN 442 ΔT${ratedDeltaTK} basis was assumed. An older BS 3528 ΔT60 ` +
        "rating for the same radiator is about 30% larger, which would flip a reuse decision.",
    });
  }
  if (emitter.ratedOutputW.provenance !== "manufacturer") {
    warnings.push({
      code: "EMITTER_RATED_OUTPUT_NOT_FROM_DATASHEET",
      severity: "warning",
      subjectId: emitter.id,
      message:
        `The rated output for "${emitter.description}" in ${room.name} is ` +
        `${emitter.ratedOutputW.provenance}, not from a manufacturer datasheet. Every number below ` +
        "is proportional to it.",
    });
  }

  const ratedOutputW = emitter.ratedOutputW.value;
  const corrected = correctEmitterOutput({
    ratedOutputW,
    flowTempC: input.proposedFlowTempC,
    returnTempC: input.proposedFlowTempC - input.systemDeltaTK,
    roomDesignTempC: room.designTempC,
    exponent,
    ratedDeltaTK,
  });

  const outputW = corrected.outputW;
  const rawShortfallW = heatLossW - outputW;
  const shortfallW = Math.max(rawShortfallW, 0);
  const surplusW = Math.max(-rawShortfallW, 0);
  const shortfallPercent = heatLossW > 0 ? (shortfallW / heatLossW) * 100 : 0;
  const outputPercentOfLoad = heatLossW > 0 ? (outputW / heatLossW) * 100 : 100;
  const meetsLoad = shortfallPercent <= tolerancePercent;

  const working = [
    `Room heat loss = ${round(heatLossW, 1)} W`,
    `Rated output = ${ratedOutputW} W at ΔT${ratedDeltaTK}` +
      `${ratedDeltaTFromDatasheet ? " (from datasheet)" : " (rating basis assumed)"}` +
      `, exponent n = ${exponent}${exponentFromDatasheet ? " (from datasheet)" : ` (default for ${radiatorKind})`}`,
    ...corrected.working,
    shortfallW > 0
      ? `Shortfall = ${round(heatLossW, 1)} − ${round(outputW, 1)} = ${round(shortfallW, 1)} W` +
        ` (${round(shortfallPercent, 1)}% of the room's heat loss)`
      : `Surplus = ${round(outputW, 1)} − ${round(heatLossW, 1)} = ${round(surplusW, 1)} W in hand` +
        ` (delivers ${round(outputPercentOfLoad, 1)}% of the load)`,
  ];

  // The lowest flow temperature at which this radiator would cover the room. Also
  // the room's contribution to the worst-performing-room test.
  let requiredFlowTempC: number | undefined;
  if (ratedOutputW > 0 && input.systemDeltaTK > 0) {
    const inverse = flowTempForRequiredOutput({
      requiredOutputW: heatLossW,
      ratedOutputW,
      roomDesignTempC: room.designTempC,
      exponent,
      ratedDeltaTK,
      systemDeltaTK: input.systemDeltaTK,
    });
    requiredFlowTempC = inverse.requiredFlowTempC;
    working.push(
      "Flow temperature this emitter would need to cover the room on its own:",
      ...inverse.working.map((w) => `  ${w}`),
    );
  } else if (!(ratedOutputW > 0)) {
    warnings.push({
      code: "EMITTER_ZERO_RATED_OUTPUT",
      severity: "compliance-failure",
      subjectId: emitter.id,
      message:
        `"${emitter.description}" in ${room.name} has a rated output of ${ratedOutputW} W, so no ` +
        "output correction or required flow temperature can be computed for it.",
    });
  }

  const reasons: string[] = [];
  let verdict: EmitterVerdict;
  let replacement: ReplacementSuggestion | undefined;

  if (meetsLoad) {
    verdict = "retain";
    reasons.push(
      `At ${input.proposedFlowTempC}°C flow (${round(corrected.meanWaterTempC, 1)}°C mean water) into a ` +
        `${room.designTempC}°C room, "${emitter.description}" delivers ${round(outputW, 0)} W against a ` +
        `${round(heatLossW, 0)} W heat loss — ${round(outputPercentOfLoad, 0)}% of what the room needs.`,
    );
    if (surplusW > 0) {
      reasons.push(
        `It has ${round(surplusW, 0)} W in hand, so it can be kept as it is.`,
      );
    }
    if (requiredFlowTempC !== undefined) {
      reasons.push(
        `It would still cover the room down to ${round(requiredFlowTempC, 1)}°C flow.`,
      );
    }
  } else {
    verdict = "replace";
    const suggestion = buildReplacementSuggestion(
      emitter,
      corrected,
      heatLossW,
      requiredFlowTempC,
    );
    replacement = suggestion;
    reasons.push(
      `At ${input.proposedFlowTempC}°C flow (${round(corrected.meanWaterTempC, 1)}°C mean water) into a ` +
        `${room.designTempC}°C room, "${emitter.description}" delivers only ${round(outputW, 0)} W against a ` +
        `${round(heatLossW, 0)} W heat loss — ${round(shortfallW, 0)} W short, ${round(shortfallPercent, 0)}% of the load.`,
      `The rated ${ratedOutputW} W applies at ΔT${ratedDeltaTK}; at ΔT${round(corrected.deltaTActualK, 1)} the ` +
        `correction factor is ${round(corrected.correctionFactor, 3)}.`,
      suggestion.note,
    );
    if (requiredFlowTempC !== undefined) {
      reasons.push(
        `Keeping it instead would mean running the system at ${round(requiredFlowTempC, 1)}°C flow, which ` +
          "costs efficiency across the whole house for the sake of one room.",
      );
    }
    warnings.push({
      code: "EMITTER_SHORTFALL_AT_PROPOSED_FLOW",
      severity: "warning",
      subjectId: room.roomId,
      message:
        `${room.name}: "${emitter.description}" is ${round(shortfallW, 0)} W (${round(shortfallPercent, 0)}%) ` +
        `short at ${input.proposedFlowTempC}°C flow.`,
    });
  }

  if (room.designTempC >= corrected.meanWaterTempC) {
    warnings.push({
      code: "MEAN_WATER_BELOW_ROOM_TEMPERATURE",
      severity: "compliance-failure",
      subjectId: room.roomId,
      message:
        `${room.name} is designed at ${room.designTempC}°C but the mean water temperature at ` +
        `${input.proposedFlowTempC}°C flow is only ${round(corrected.meanWaterTempC, 1)}°C. No wet emitter ` +
        "can heat this room at this flow temperature.",
    });
  }

  return {
    roomId: room.roomId,
    roomName: room.name,
    designTempC: room.designTempC,
    heatLossW,
    emitterId: emitter.id,
    emitterDescription: emitter.description,
    modelledAs: "radiator",
    outputAtProposedFlowW: outputW,
    outputPercentOfLoad: round(outputPercentOfLoad),
    shortfallW: round(shortfallW),
    shortfallPercent: round(shortfallPercent),
    surplusW: round(surplusW),
    meetsLoad,
    requiredFlowTempC,
    verdict,
    reasons,
    working,
    replacement,
  };
}

function buildReplacementSuggestion(
  emitter: RadiatorEmitter,
  corrected: CorrectedEmitterOutput,
  heatLossW: number,
  requiredFlowTempC: number | undefined,
): ReplacementSuggestion {
  // The replacement is sized by inverting the same correction factor: whatever the
  // room needs at the actual ΔT, divided by the factor, is the rated figure to shop
  // for. No product database is needed to state that.
  const requiredRatedOutputW =
    corrected.correctionFactor > 0 ? heatLossW / corrected.correctionFactor : Infinity;
  const existingRatedW = emitter.ratedOutputW.value;
  const upliftFactor =
    existingRatedW > 0 ? requiredRatedOutputW / existingRatedW : Infinity;

  const suggestedMinimumLengthMm =
    emitter.lengthMm !== undefined && Number.isFinite(upliftFactor)
      ? Math.ceil(emitter.lengthMm * upliftFactor)
      : undefined;

  const parts: string[] = [
    `A replacement needs to be rated at least ${round(requiredRatedOutputW, 0)} W at ` +
      `ΔT${corrected.ratedDeltaTK} — ${round(upliftFactor, 2)}× the existing ${existingRatedW} W.`,
  ];

  if (suggestedMinimumLengthMm !== undefined) {
    parts.push(
      `Keeping the same type and ${emitter.heightMm !== undefined ? `${emitter.heightMm} mm ` : ""}height, ` +
        `and taking output as proportional to length, that is at least ${suggestedMinimumLengthMm} mm ` +
        `against the existing ${emitter.lengthMm} mm — round up to the next stock length and confirm ` +
        "against the chosen product's datasheet.",
    );
  } else {
    parts.push(
      "Record the existing radiator's height and length to get a suggested replacement size.",
    );
  }

  if (upliftFactor > SAME_TYPE_UPLIFT_PRACTICAL_LIMIT) {
    parts.push(
      `An uplift of ${round(upliftFactor, 1)}× rarely fits the same wall. Expect to move up a type ` +
        "(a double-panel double-convector in place of a single), use a taller radiator, or add a " +
        "second emitter in the room.",
    );
  }

  if (requiredFlowTempC !== undefined) {
    parts.push(
      `The alternative to replacing it is running the system at ${round(requiredFlowTempC, 1)}°C flow.`,
    );
  }

  const suggestion: ReplacementSuggestion = {
    requiredRatedOutputW: round(requiredRatedOutputW),
    ratedDeltaTK: corrected.ratedDeltaTK,
    upliftFactor: round(upliftFactor),
    suggestedMinimumLengthMm,
    alternativeFlowTempC: requiredFlowTempC,
    note: parts.join(" "),
  };
  return suggestion;
}

function assessUnderfloorRoom(
  room: RoomHeatLoss,
  emitter: UnderfloorEmitter,
  input: EmitterAssessmentInput,
  warnings: CalcWarning[],
): RoomEmitterAssessment {
  const heatLossW = room.totalW;
  const ceiling = underfloorOutputCeiling({
    heatedFloorAreaM2: emitter.heatedFloorAreaM2,
    roomDesignTempC: room.designTempC,
    zone: emitter.zone,
  });

  const working: string[] = [
    `Room heat loss = ${round(heatLossW, 1)} W`,
    "Underfloor heating is not corrected with the BS EN 442 radiator exponent: its output is capped by floor surface temperature, not by water temperature.",
    ...ceiling.working,
  ];

  const reasons: string[] = [];

  // The hard physical check, and the one that does not need any construction data:
  // if the load exceeds what the floor can emit at its surface temperature limit,
  // no flow temperature and no manufacturer's design can rescue it.
  if (heatLossW > ceiling.maxOutputW) {
    warnings.push({
      code: "UFH_LOAD_EXCEEDS_SURFACE_TEMPERATURE_LIMIT",
      severity: "compliance-failure",
      subjectId: room.roomId,
      message:
        `${room.name} needs ${round(heatLossW, 0)} W but ${emitter.heatedFloorAreaM2} m² of heated floor ` +
        `can emit at most ${round(ceiling.maxOutputW, 0)} W (${round(ceiling.maxSpecificOutputWPerM2, 0)} W/m²) ` +
        `before exceeding the BS EN 1264-3 ${ceiling.maxSurfaceTempC}°C surface limit for a "${ceiling.zone}" ` +
        "area. Raising the flow temperature cannot close this gap — the room needs supplementary emitters, " +
        "more heated floor area, or fabric improvement.",
    });
    reasons.push(
      `The heated floor in ${room.name} cannot meet the room's ${round(heatLossW, 0)} W at any flow ` +
        `temperature: at the ${ceiling.maxSurfaceTempC}°C surface limit it emits ${round(ceiling.maxOutputW, 0)} W.`,
      "This is a limit of comfort and floor coverings, not of the heat pump, so it is not fixed by running hotter.",
    );

    return {
      roomId: room.roomId,
      roomName: room.name,
      designTempC: room.designTempC,
      heatLossW,
      emitterId: emitter.id,
      emitterDescription: emitter.description,
      modelledAs: "underfloor",
      outputAtProposedFlowW: ceiling.maxOutputW,
      outputPercentOfLoad:
        heatLossW > 0 ? round((ceiling.maxOutputW / heatLossW) * 100) : 100,
      shortfallW: round(heatLossW - ceiling.maxOutputW),
      shortfallPercent:
        heatLossW > 0 ? round(((heatLossW - ceiling.maxOutputW) / heatLossW) * 100) : 0,
      surplusW: 0,
      meetsLoad: false,
      verdict: "replace",
      reasons,
      working,
      underfloorCeiling: ceiling,
    };
  }

  const design = emitter.manufacturerDesign;
  if (design === undefined) {
    // Refusing rather than guessing. Getting from mean water temperature to floor
    // surface temperature needs pipe pitch, screed cover and conductivity, and the
    // floor covering's thermal resistance. None of it is in a survey, and a guess
    // here would be written into the design pack as the §3.8.1(d) figure.
    warnings.push({
      code: "UFH_FLOW_TEMPERATURE_UNDETERMINED",
      severity: "compliance-failure",
      subjectId: room.roomId,
      message:
        `${room.name} is heated by an underfloor circuit with no manufacturer design data. This engine ` +
        "will not derive an underfloor flow temperature from survey geometry: it depends on pipe pitch " +
        "and diameter, screed cover and conductivity, and the floor covering's thermal resistance, which " +
        "on its own can move the answer by 10 K. Supply the underfloor designer's output at their stated " +
        "flow and return, or the design emitter temperature required by MIS 3005-D §3.8.1(d) cannot be " +
        "stated for this system.",
    });
    reasons.push(
      `${room.name} can physically be heated by its floor — the ${emitter.heatedFloorAreaM2} m² circuit can ` +
        `emit up to ${round(ceiling.maxOutputW, 0)} W against a ${round(heatLossW, 0)} W load.`,
      "The flow temperature it needs has not been calculated here, because that depends on the floor construction and covering rather than on anything the survey measured.",
      "Obtain the underfloor designer's output at their stated flow and return before the design pack is issued.",
    );

    return {
      roomId: room.roomId,
      roomName: room.name,
      designTempC: room.designTempC,
      heatLossW,
      emitterId: emitter.id,
      emitterDescription: emitter.description,
      modelledAs: "underfloor",
      shortfallW: 0,
      shortfallPercent: 0,
      surplusW: 0,
      meetsLoad: false,
      verdict: "undetermined",
      reasons,
      working,
      underfloorCeiling: ceiling,
    };
  }

  const designMeanC = (design.flowTempC + design.returnTempC) / 2;
  working.push(
    `Underfloor designer's figure: ${design.outputW} W at ${design.flowTempC}/${design.returnTempC} °C ` +
      `(mean ${round(designMeanC, 1)} °C), source ${design.source}`,
  );

  const meetsAtDesignFlow = design.outputW >= heatLossW;
  const proposedCoversDesignFlow = input.proposedFlowTempC >= design.flowTempC;
  const rawShortfallW = heatLossW - design.outputW;

  if (!meetsAtDesignFlow) {
    warnings.push({
      code: "UFH_MANUFACTURER_OUTPUT_BELOW_LOAD",
      severity: "warning",
      subjectId: room.roomId,
      message:
        `${room.name}: the underfloor design provides ${design.outputW} W at ${design.flowTempC}°C flow ` +
        `against a ${round(heatLossW, 0)} W heat loss — ${round(rawShortfallW, 0)} W short. The circuit has ` +
        `headroom to the surface temperature limit (${round(ceiling.maxOutputW, 0)} W), so ask the underfloor ` +
        "designer to re-run it at a closer pipe pitch or a higher flow temperature rather than assuming " +
        "either will work.",
    });
    reasons.push(
      `The underfloor design supplied for ${room.name} delivers ${design.outputW} W at ` +
        `${design.flowTempC}°C flow, ${round(rawShortfallW, 0)} W short of the ${round(heatLossW, 0)} W load.`,
      `The floor itself has headroom — it could emit up to ${round(ceiling.maxOutputW, 0)} W at the ` +
        `${ceiling.maxSurfaceTempC}°C surface limit — so this is a circuit design to revisit, not a room to write off.`,
      "No flow temperature is stated for this room until a design that meets the load is supplied.",
    );

    return {
      roomId: room.roomId,
      roomName: room.name,
      designTempC: room.designTempC,
      heatLossW,
      emitterId: emitter.id,
      emitterDescription: emitter.description,
      modelledAs: "underfloor",
      outputAtProposedFlowW: design.outputW,
      outputPercentOfLoad: heatLossW > 0 ? round((design.outputW / heatLossW) * 100) : 100,
      shortfallW: round(rawShortfallW),
      shortfallPercent: heatLossW > 0 ? round((rawShortfallW / heatLossW) * 100) : 0,
      surplusW: 0,
      meetsLoad: false,
      verdict: "undetermined",
      reasons,
      working,
      underfloorCeiling: ceiling,
    };
  }

  if (!proposedCoversDesignFlow) {
    warnings.push({
      code: "UFH_DESIGN_FLOW_ABOVE_PROPOSED",
      severity: "warning",
      subjectId: room.roomId,
      message:
        `${room.name}: the underfloor design is stated at ${design.flowTempC}°C flow, above the proposed ` +
        `${input.proposedFlowTempC}°C. Its output at the lower temperature has NOT been extrapolated — ` +
        "the BS EN 442 exponent does not apply to a floor. The circuit sets the system flow temperature " +
        `at ${design.flowTempC}°C unless the underfloor designer re-runs it lower.`,
    });
  }

  reasons.push(
    `${room.name} is heated by its floor. The underfloor design (${design.source}) gives ${design.outputW} W ` +
      `at ${design.flowTempC}/${design.returnTempC}°C against a ${round(heatLossW, 0)} W load, so the circuit is kept.`,
    `That flow temperature is taken as stated, not corrected — a floor's output is capped by its ` +
      `${ceiling.maxSurfaceTempC}°C surface limit (${round(ceiling.maxOutputW, 0)} W here), so the radiator power law does not apply to it.`,
  );

  const surplusW = design.outputW - heatLossW;

  return {
    roomId: room.roomId,
    roomName: room.name,
    designTempC: room.designTempC,
    heatLossW,
    emitterId: emitter.id,
    emitterDescription: emitter.description,
    modelledAs: "underfloor",
    outputAtProposedFlowW: design.outputW,
    outputPercentOfLoad: heatLossW > 0 ? round((design.outputW / heatLossW) * 100) : 100,
    shortfallW: 0,
    shortfallPercent: 0,
    surplusW: round(surplusW),
    meetsLoad: true,
    // The underfloor designer's stated flow temperature IS this room's requirement.
    // It is not interpolated to any other temperature, because it cannot be.
    requiredFlowTempC: design.flowTempC,
    verdict: "retain",
    reasons,
    working,
    underfloorCeiling: ceiling,
  };
}
